
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FeedbackModal } from "./FeedbackModal";
import { LoadingOverlay } from "./LoadingOverlay";
import { LeadCaptureModal } from "./LeadCaptureModal";
import { SEOFormInputs } from "./seo/SEOFormInputs";
import { SEOFormActions } from "./seo/SEOFormActions";
import { analyzeSEO, saveAnalysis } from "@/services/seoAnalyzer";
import { hubspotService } from "@/services/hubspotService";

export function SEOForm() {
  const [loading, setLoading] = useState(false);
  const [url, setUrl] = useState("");
  const [keyword, setKeyword] = useState("");
  const [showFeedback, setShowFeedback] = useState(false);
  const [showLeadCapture, setShowLeadCapture] = useState(false);
  const [pendingAnalysis, setPendingAnalysis] = useState<{ url: string; keyword: string } | null>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const urlParam = searchParams.get('url');
    const keywordParam = searchParams.get('keyword');

    if (urlParam) {
      try {
        // Decodifica e valida a URL
        const decodedUrl = decodeURIComponent(urlParam);
        new URL(decodedUrl); // Isso lançará um erro se a URL for inválida
        setUrl(decodedUrl);
      } catch (error) {
        console.error("URL inválida nos parâmetros:", error);
        toast.error("URL inválida nos parâmetros");
        return;
      }
    }

    if (keywordParam) {
      const decodedKeyword = decodeURIComponent(keywordParam);
      setKeyword(decodedKeyword);
    }

    // Inicia a análise apenas se ambos os parâmetros estiverem presentes e válidos
    if (urlParam && keywordParam && !loading) {
      const decodedUrl = decodeURIComponent(urlParam);
      const decodedKeyword = decodeURIComponent(keywordParam);
      
      try {
        new URL(decodedUrl); // Valida a URL
        handleAnalysis(decodedUrl.trim(), decodedKeyword.trim());
      } catch (error) {
        console.error("URL inválida:", error);
        toast.error("URL inválida nos parâmetros");
      }
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAnalysis = async (analysisUrl: string, analysisKeyword: string) => {
    if (loading) return; // Evita múltiplas chamadas simultâneas
    
    setLoading(true);
    console.log("Iniciando análise com:", { analysisUrl, analysisKeyword });

    try {
      // Tenta obter o email do lead do localStorage se disponível
      const processedLeads = JSON.parse(localStorage.getItem('processedLeads') || '[]');
      const lastProcessedEmail = processedLeads.length > 0 ? processedLeads[processedLeads.length - 1] : undefined;
      
      const data = await analyzeSEO(analysisUrl, analysisKeyword, lastProcessedEmail);
      console.log("Resposta da análise:", data);
      
      if (!data || !data.id) {
        throw new Error("Formato de resposta inválido");
      }

      const seoScore = data.analysis_result?.url_analyses?.[0]?.seo_score;
      saveAnalysis(data.id, url, keyword, seoScore);
      navigate(`/relatorio/${data.id}`);
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error(
        error instanceof Error 
          ? error.message 
          : "Erro ao analisar URL. Por favor, tente novamente em alguns instantes."
      );
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const trimmedUrl = url.trim();
    const trimmedKeyword = keyword.trim();

    // Valida os campos
    if (!trimmedUrl || !trimmedKeyword) {
      toast.error("Por favor, preencha todos os campos obrigatórios.");
      return;
    }

    // Verifica se já existe algum lead processado (cache)
    if (hubspotService.hasAnyProcessedLead()) {
      // Usuário já preencheu o formulário antes, pode fazer a análise diretamente
      console.log("Usuário já processado, executando análise diretamente");
      await handleAnalysis(trimmedUrl, trimmedKeyword);
    } else {
      // Primeira vez, precisa capturar os dados do lead
      console.log("Primeira vez do usuário, solicitando dados do lead");
      setPendingAnalysis({ url: trimmedUrl, keyword: trimmedKeyword });
      setShowLeadCapture(true);
    }
  };

  const handleLeadCaptureSuccess = async () => {
    // Após o sucesso da captura de leads, executa a análise
    if (pendingAnalysis) {
      console.log("Lead capturado com sucesso, executando análise");
      await handleAnalysis(pendingAnalysis.url, pendingAnalysis.keyword);
      setPendingAnalysis(null);
    }
  };

  const handleLeadCaptureClose = () => {
    setShowLeadCapture(false);
    setPendingAnalysis(null);
  };

  return (
    <>
      {loading && <LoadingOverlay />}
      <form onSubmit={handleSubmit} className="space-y-6 max-w-lg mx-auto">
        <SEOFormInputs
          url={url}
          keyword={keyword}
          onUrlChange={setUrl}
          onKeywordChange={setKeyword}
        />
        <SEOFormActions
          loading={loading}
          onFeedbackClick={() => setShowFeedback(true)}
        />
      </form>
      
      <LeadCaptureModal
        open={showLeadCapture}
        onOpenChange={handleLeadCaptureClose}
        onSuccess={handleLeadCaptureSuccess}
      />
      
      <FeedbackModal
        open={showFeedback}
        onOpenChange={setShowFeedback}
        urlAnalyzed={url}
        keyword={keyword}
      />
    </>
  );
}
